// src/NewsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ChevronRight, X, Calendar, Loader, Newspaper, Clock, TrendingUp, CheckCircle } from 'lucide-react';
import { API } from './utils/database-frontend.js';

const NewsPage = () => {
  const [selectedNews, setSelectedNews] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const statsRef = useRef(null);
  const newsRef = useRef(null);
  
  const isStatsInView = useInView(statsRef, { once: true, threshold: 0.2 });
  const isNewsInView = useInView(newsRef, { once: true, threshold: 0.1 });

  const stats = [
    {
      icon: Newspaper,
      title: "Yayınlanan Haber",
      count: "50+",
      description: "Güncel haber ve duyurular",
      gradient: "from-brand-orange to-orange-500"
    },
    {
      icon: TrendingUp,
      title: "Güncel Gelişmeler",
      count: "24/7",
      description: "Sürekli güncellenen içerik",
      gradient: "from-brand-navy to-blue-600"
    },
    {
      icon: Clock,
      title: "Hızlı Bilgilendirme",
      count: "Anında",
      description: "Gerçek zamanlı haberler",
      gradient: "from-brand-gray to-gray-600"
    },
    {
      icon: CheckCircle,
      title: "Doğrulanmış İçerik",
      count: "%100",
      description: "Güvenilir haber kaynağı",
      gradient: "from-brand-orange to-red-500"
    }
  ];

  // Fetch news from database
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const fetchedNews = await API.news.getAll();
        console.log('Fetched news from API:', fetchedNews);
        console.log('First news published_date:', fetchedNews[0]?.published_date);
        console.log('First news publishedDate:', fetchedNews[0]?.publishedDate);
        setNews(fetchedNews);
        setError(null);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Haberler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setNews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const openModal = (article) => {
    setSelectedNews(article);
  };

  const closeModal = () => {
    setSelectedNews(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih belirtilmemiş';
    
    try {
      // Handle different date formats
      let date;
      if (typeof dateString === 'string') {
        // If it's just a date string (YYYY-MM-DD), add time to make it valid
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          date = new Date(dateString + 'T00:00:00');
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return 'Geçersiz tarih';
      }
      
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, dateString);
      return 'Tarih formatı hatası';
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    try {
      let date;
      if (typeof dateString === 'string') {
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          date = new Date(dateString + 'T00:00:00');
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return '1 gün önce';
      if (diffDays < 7) return `${diffDays} gün önce`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta önce`;
      return `${Math.floor(diffDays / 30)} ay önce`;
    } catch (error) {
      console.error('Time ago calculation error:', error, dateString);
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-brand-navy via-brand-gray to-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        {/* Animated Background Shapes */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.3, 1]
          }}
          transition={{ 
            duration: 28,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 right-20 w-44 h-44 bg-brand-orange opacity-10 rounded-full"
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 22,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 left-20 w-36 h-36 bg-white opacity-5 rounded-full"
        />

        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-brand-orange rounded-full mb-6">
              <Newspaper className="w-12 h-12 text-white" />
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
          >
            Haberler
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Şirketimizden <span className="text-brand-orange font-semibold">güncel haberler</span>, 
            gelişmeler ve sektör haberleri.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <div className="flex items-center bg-white bg-opacity-10 backdrop-blur-sm rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-brand-orange mr-2" />
              <span className="text-white">Güncel Haberler</span>
            </div>
            <div className="flex items-center bg-white bg-opacity-10 backdrop-blur-sm rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-brand-orange mr-2" />
              <span className="text-white">Sektör Gelişmeleri</span>
            </div>
          </motion.div>
        </div>
      </section>



      {/* News Section */}
      <section ref={newsRef} className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isNewsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-brand-navy mb-4">Son Haberler</h2>
            <p className="text-xl text-brand-gray max-w-2xl mx-auto">
              Şirketimizden ve sektörden en güncel haberler
            </p>
          </motion.div>

          {/* Loading State */}
          {loading && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative">
                <Loader className="w-12 h-12 text-brand-orange animate-spin" />
                <div className="absolute inset-0 w-12 h-12 border-2 border-brand-orange border-t-transparent rounded-full animate-ping"></div>
              </div>
              <span className="mt-4 text-brand-gray text-lg font-medium">Haberler yükleniyor...</span>
              <p className="text-brand-gray text-sm mt-2">En güncel haberleri getiriyoruz</p>
            </motion.div>
          )}

          {/* Error State */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">Bir Hata Oluştu</h3>
                <p className="text-red-600">{error}</p>
              </div>
            </motion.div>
          )}

          {/* News Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="col-span-full text-center py-20"
                >
                  <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-12 max-w-md mx-auto">
                    <Newspaper className="w-16 h-16 text-brand-gray mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold text-brand-gray mb-2">Henüz Haber Yok</h3>                   
                  </div>
                </motion.div>
              ) : (
                news.map((article, index) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    animate={isNewsInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer group border border-gray-100"
                    onClick={() => openModal(article)}
                  >
                    <div className="relative overflow-hidden">
                      <img 
                        src={article.imageUrl || article.image_url || 'https://placehold.co/800x400/f4712f/ffffff?text=Haber+Görseli'} 
                        alt={article.title}
                        className="w-full h-64 object-contain bg-gradient-to-br from-gray-50 to-gray-100 group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/800x400/f4712f/ffffff?text=Görsel+Bulunamadı';
                        }}
                      />
                      <div className="absolute top-4 right-4 bg-brand-orange text-white px-3 py-1 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Haberi Oku
                      </div>
                      <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-white text-xs">{getTimeAgo(article.publishedDate || article.published_date)}</span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center text-sm text-brand-orange mb-3">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>{formatDate(article.publishedDate || article.published_date)}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-brand-navy mb-3 line-clamp-2 group-hover:text-brand-orange transition-colors duration-300">
                        {article.title}
                      </h3>
                      
                      <p className="text-brand-gray mb-4 line-clamp-3 leading-relaxed">
                        {article.description.length > 150 
                          ? article.description.substring(0, 150) + '...' 
                          : article.description
                        }
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-brand-orange font-semibold text-sm">Şirket Haberi</span>
                        <button className="text-brand-orange font-semibold flex items-center hover:text-orange-600 transition duration-300 group-hover:translate-x-1">
                          Devamını Oku
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* News Modal */}
      <AnimatePresence>
        {selectedNews && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl max-w-5xl w-full max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative">
                <div className="bg-gradient-to-br from-brand-navy to-brand-gray p-6 rounded-t-3xl">
                  <img 
                    src={selectedNews.imageUrl || selectedNews.image_url || 'https://placehold.co/800x400/f4712f/ffffff?text=Haber+Görseli'} 
                    alt={selectedNews.title}
                    className="w-full h-80 object-contain bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/800x400/f4712f/ffffff?text=Görsel+Bulunamadı';
                    }}
                  />
                </div>
                <button
                  onClick={closeModal}
                  className="absolute top-8 right-8 bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-3 hover:bg-opacity-30 transition duration-300 text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="bg-brand-orange rounded-full p-2 mr-4">
                    <Newspaper className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center text-sm text-brand-orange">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{formatDate(selectedNews.publishedDate || selectedNews.published_date)}</span>
                  </div>
                </div>
                
                <h2 className="text-4xl font-bold text-brand-navy mb-6 break-words leading-tight">
                  {selectedNews.title}
                </h2>
                
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 mb-6">
                  <div className="text-brand-gray text-lg leading-relaxed break-words whitespace-pre-line">
                    {selectedNews.description}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-brand-orange to-orange-500 rounded-2xl p-4 text-white text-center">
                    <Newspaper className="w-8 h-8 mx-auto mb-2" />
                    <p className="font-semibold">Güncel Haber</p>
                  </div>
                  <div className="bg-gradient-to-br from-brand-navy to-blue-600 rounded-2xl p-4 text-white text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                    <p className="font-semibold">Doğrulanmış</p>
                  </div>
                  <div className="bg-gradient-to-br from-brand-gray to-gray-600 rounded-2xl p-4 text-white text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2" />
                                          <p className="font-semibold">{getTimeAgo(selectedNews.publishedDate || selectedNews.published_date)}</p>
                  </div>
                </div>
                
                {/* Close Button */}
                <div className="flex justify-center">
                  <button 
                    onClick={closeModal}
                    className="bg-gradient-to-r from-brand-orange to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-12 rounded-2xl transition duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-navy to-brand-gray text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Haberlerden Haberdar Olun</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-300">
              Şirketimizden ve sektörden en güncel gelişmeleri kaçırmayın.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="/contact"
                className="inline-block bg-brand-orange hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-2xl transition duration-300 shadow-lg"
              >
                Bizimle İletişime Geç
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default NewsPage;